import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import ProjectEditModal from '../projects/modal/EditProject/modal';
import { v4 as uuid } from 'uuid';
import { withTranslation } from 'react-i18next';
import { getMainDomain, normalizeDomain } from '../../../helpers/normalizeDomain';

// Approximate dropdown width, used to keep it inside the viewport before it is
// rendered (and therefore before it can be measured).
const DROPDOWN_WIDTH = 220;
const VIEWPORT_PADDING = 8;

class Action extends Component {
    constructor() {
        super();

        this.onOpenEditModal = this.onOpenEditModal.bind(this);
        this.onCloseEditModal = this.onCloseEditModal.bind(this);
        this.onToggleDropdown = this.onToggleDropdown.bind(this);
        this.onCloseDropdown = this.onCloseDropdown.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.onSelectAddType = this.onSelectAddType.bind(this);

        this.buttonRef = React.createRef();
        this.dropdownRef = React.createRef();

        this.state = {
            isEditModalOpen: false,
            isDropdownOpen: false,
            dropdownPosition: null,
            addType: null,
        };
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        // The dropdown is positioned with fixed coordinates taken from the button,
        // so it has to close as soon as the page (or a table) scrolls or resizes.
        window.addEventListener('scroll', this.onCloseDropdown, true);
        window.addEventListener('resize', this.onCloseDropdown);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        window.removeEventListener('scroll', this.onCloseDropdown, true);
        window.removeEventListener('resize', this.onCloseDropdown);
    }

    handleClickOutside(event) {
        if (!this.state.isDropdownOpen) return;
        if (this.dropdownRef.current?.contains(event.target)) return;
        if (this.buttonRef.current?.contains(event.target)) return;
        this.onCloseDropdown();
    }

    onOpenEditModal() {
        if (this.props.openEditModal) {
            return this.props.openEditModal();
        }
        this.setState({ isEditModalOpen: true });
    }
    onCloseEditModal() {
        if (this.props.closeEditModal) {
            return this.props.closeEditModal();
        }
        this.setState({ isEditModalOpen: false, addType: null });
    }

    onToggleDropdown() {
        if (this.state.isDropdownOpen) {
            return this.onCloseDropdown();
        }

        const rect = this.buttonRef.current?.getBoundingClientRect();
        if (!rect) return;

        this.setState({
            isDropdownOpen: true,
            dropdownPosition: {
                top: rect.bottom + 4,
                left: Math.max(
                    VIEWPORT_PADDING,
                    Math.min(rect.left, window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_PADDING),
                ),
            },
        });
    }
    onCloseDropdown() {
        if (!this.state.isDropdownOpen) return;
        this.setState({ isDropdownOpen: false, dropdownPosition: null });
    }

    onSelectAddType(addType) {
        this.setState(
            { addType, isDropdownOpen: false, dropdownPosition: null },
            this.onOpenEditModal,
        );
    }

    render() {
        const { t, showAll } = this.props;

        const isIncludedInCompetitors = this.props.project?.competitors?.some(
            (competitor) =>
                getMainDomain(competitor.domain) === getMainDomain(this.props.domain) ||
                competitor.name?.toLowerCase() === this.props.brand?.toLowerCase(),
        );
        const isIncludedInClients = this.props.project?.clients?.some(
            (client) =>
                getMainDomain(client.domain) === getMainDomain(this.props.domain) ||
                client.name?.toLowerCase() === this.props.brand?.toLowerCase(),
        );
        const isOwnDomain =
            getMainDomain(this.props.project?.own_domain) === getMainDomain(this.props.domain) ||
            this.props.brand?.toLowerCase() === this.props.project?.own_product_name?.toLowerCase();

        const isAlreadyAdded = isOwnDomain || isIncludedInCompetitors || isIncludedInClients;

        const newItem = {
            domain: this.props.domain ?? '',
            name: this.props.brand ?? '',
            id: uuid(),
        };

        return (
            <div>
                {showAll ? (
                    <div className="d-flex gap-8">
                        <a
                            href={`/ai-tracking/project/${this.props.project?.id}/settings/`}
                            className="ai-tracking-edit-project-btn"
                            title={t('ai_tracking.projects.settings.header')}
                        >
                            <i className="icon-settings1" />
                        </a>
                        <button
                            onClick={() => this.props.forceRunAll()}
                            className="ai-tracking-edit-project-btn with-refresh"
                            title="Force Run All"
                        >
                            <i className="icon-Frame-converted" />
                        </button>
                    </div>
                ) : (
                    <button
                        ref={this.buttonRef}
                        className="ai-tracking-action-btn table-action-main-btn"
                        title={t('ai_tracking.global.add')}
                        onClick={!isAlreadyAdded ? this.onToggleDropdown : undefined}
                    >
                        {!isAlreadyAdded ? (
                            <i className="icon-plus" />
                        ) : (
                            <i className="icon-check-icon" />
                        )}{' '}
                        {isOwnDomain
                            ? t('ai_tracking.global.own')
                            : isIncludedInCompetitors
                            ? t('ai_tracking.global.competitor')
                            : isIncludedInClients
                            ? t('ai_tracking.global.client')
                            : t('ai_tracking.global.add')}
                    </button>
                )}
                {!showAll &&
                    this.state.isDropdownOpen &&
                    createPortal(
                        <div
                            ref={this.dropdownRef}
                            className="add-type-dropdown"
                            style={{
                                top: this.state.dropdownPosition.top,
                                left: this.state.dropdownPosition.left,
                            }}
                        >
                            <button
                                type="button"
                                className="add-type-choice-btn"
                                onClick={() => this.onSelectAddType('competitor')}
                            >
                                <i className="icon-plus" />{' '}
                                {t('ai_tracking.global.add_as_competitor')}
                            </button>
                            <button
                                type="button"
                                className="add-type-choice-btn"
                                onClick={() => this.onSelectAddType('client')}
                            >
                                <i className="icon-plus" /> {t('ai_tracking.global.add_as_client')}
                            </button>
                        </div>,
                        document.body,
                    )}
                {this.props.project &&
                    (this.props.isEditModalOpen || this.state.isEditModalOpen) && (
                        <ProjectEditModal
                            isOpen={this.props.isEditModalOpen || this.state.isEditModalOpen}
                            onClose={this.onCloseEditModal}
                            item={this.props.project}
                            newCompetitor={
                                !showAll && this.state.addType === 'competitor'
                                    ? newItem
                                    : undefined
                            }
                            newClient={
                                !showAll && this.state.addType === 'client' ? newItem : undefined
                            }
                            updateData={(_, __, project) => this.props.updateProject(project)}
                            fromSingleProject={!showAll}
                        />
                    )}
            </div>
        );
    }
}

export default withTranslation()(Action);
